/**
 * Favoris et récents — l'accès rapide du §6.3-a, **scindé par catégorie**.
 *
 * ⚠️ Un seul aller-retour pour toutes les listes (§6.2 : « les listes d'accès
 * rapide sont servies par un endpoint unique et mises en cache par TanStack
 * Query »). Ce panneau est monté deux fois — une fois sous « Aliment », une fois
 * sous « Recette » — mais les deux partagent la même requête en cache : le
 * second montage ne coûte rien.
 *
 * ⚠️ **Favoris avant récents.** Un favori est un choix délibéré, un récent un
 * accident de l'historique. L'ordre était inverse jusqu'ici parce que les deux
 * listes se suivaient dans un écran unique ; maintenant qu'elles sont seules
 * dans leur catégorie, la plus intentionnelle passe en tête.
 *
 * ⚠️ « Récents » veut dire **derniers utilisés**, pas derniers créés : le tri
 * vient de la dernière entrée de journal qui référence l'aliment. C'est le
 * serveur qui le fait ; rien ici ne retrie.
 *
 * ⚠️ Les recettes arrivent **complètes**, ingrédients compris (étape 7). C'est
 * ce qui permet d'enchaîner sur l'écran d'ajustement (specs §6.5-2) sans un
 * aller-retour de plus au milieu du chemin critique.
 */

import { useQuery } from '@tanstack/react-query';

import { readError } from '../../api/client';
import { fetchQuickAccess, QUICK_ACCESS_KEY } from '../../api/foods';
import { Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import type { AddPick } from './addTypes';
import { PersonalFoodRow, PersonalRecipeRow } from './FoodRow';

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <h3
        className="n-eyebrow"
        style={{ margin: 0 }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function List({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>{children}</div>
  );
}

export function QuickAccessPanel({
  scope,
  onPick,
}: {
  scope: 'food' | 'recipe';
  onPick: (pick: AddPick) => void;
}) {
  const quick = useQuery({ queryKey: QUICK_ACCESS_KEY, queryFn: fetchQuickAccess });

  // 🔴 Les données d'abord : une erreur de TRANSPORT ne doit pas masquer un
  // cache valide (V2 §3.5). Voir `readError` dans `api/client.ts`.
  const lecture = readError(quick);
  if (lecture) return <ReadNote error={lecture} quoi="l’accès rapide" />;
  if (quick.data === undefined) return <Hint>Chargement…</Hint>;

  const { recentFoods, favoriteFoods, recentRecipes, favoriteRecipes } = quick.data;

  if (scope === 'recipe') {
    // Une recette favorite est déjà dans « Favoris » : la répéter dans
    // « Récentes » ferait deux lignes identiques à trois centimètres d'écart.
    const favoriteIds = new Set(favoriteRecipes.map((recipe) => recipe.id));
    const recentOnly = recentRecipes.filter((recipe) => !favoriteIds.has(recipe.id));

    if (favoriteRecipes.length === 0 && recentOnly.length === 0) {
      return (
        <Hint>
          Aucune recette encore utilisée. Les recettes se créent dans la Bibliothèque ; celles
          qu’on emploie souvent gagnent à être mises en favori.
        </Hint>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        {favoriteRecipes.length > 0 && (
          <Section title="Favorites">
            <List>
              {favoriteRecipes.map((recipe) => (
                <PersonalRecipeRow
                  key={recipe.id}
                  recipe={recipe}
                  onPick={() => {
                    onPick({ kind: 'recipe', recipe });
                  }}
                />
              ))}
            </List>
          </Section>
        )}

        {recentOnly.length > 0 && (
          <Section title="Récentes">
            <List>
              {recentOnly.map((recipe) => (
                <PersonalRecipeRow
                  key={recipe.id}
                  recipe={recipe}
                  onPick={() => {
                    onPick({ kind: 'recipe', recipe });
                  }}
                />
              ))}
            </List>
          </Section>
        )}
      </div>
    );
  }

  const favoriteIds = new Set(favoriteFoods.map((food) => food.id));
  const recentOnly = recentFoods.filter((food) => !favoriteIds.has(food.id));

  if (favoriteFoods.length === 0 && recentOnly.length === 0) {
    return (
      <Hint>
        Rien encore. Chercher un aliment, scanner un code-barres, ou saisir directement les macros
        avec « Saisie libre ». Les aliments utilisés apparaîtront ici.
      </Hint>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
      {favoriteFoods.length > 0 && (
        <Section title="Favoris">
          <List>
            {favoriteFoods.map((food) => (
              <PersonalFoodRow
                key={food.id}
                food={food}
                onPick={() => {
                  onPick({ kind: 'food', food });
                }}
              />
            ))}
          </List>
        </Section>
      )}

      {recentOnly.length > 0 && (
        <Section title="Récents">
          <List>
            {recentOnly.map((food) => (
              <PersonalFoodRow
                key={food.id}
                food={food}
                onPick={() => {
                  onPick({ kind: 'food', food });
                }}
              />
            ))}
          </List>
        </Section>
      )}
    </div>
  );
}
